/**
 * Which clarifying-question round, if any, holds the composer's slot (spec
 * "Frontend" → Placement and Reload).
 *
 * A round docks only when it is on the LAST turn of the transcript, and never
 * mid-stream — the lead-in finishes first. The last turn is the newest pending
 * local turn (local turns not yet retired by a persisted id: the same dedupe
 * MessageList applies), else the newest persisted row. A persisted round docks
 * only while `open`; answered, skipped and dismissed rounds render nothing
 * extra — the summary bubble is the record. A round closed on this client
 * (submitted, dismissed, picked) never docks again from a stale copy.
 */
import type { LocalTurn } from "@/features/chat/chat-store";
import type { GuestStopCause } from "@/features/chat/guest/allowance";
import type { ThreadMessage } from "@/features/chat/MessageList";

import type { ClarifyKind, ClarifyRoundEvent } from "./types";

export interface OpenRoundInput {
  messages: readonly ThreadMessage[];
  localTurns: readonly LocalTurn[];
  closedRoundIds: Readonly<Record<string, true>>;
  isStreaming: boolean;
}

export function selectOpenRound({
  messages,
  localTurns,
  closedRoundIds,
  isStreaming,
}: OpenRoundInput): ClarifyRoundEvent | null {
  if (isStreaming) return null;
  const persistedIds = new Set(messages.map((message) => message.id));
  const pending = localTurns.filter(
    (turn) => turn.messageId === undefined || !persistedIds.has(turn.messageId),
  );
  let round: ClarifyRoundEvent | null;
  const lastLocal = pending[pending.length - 1];
  if (lastLocal !== undefined) {
    if (lastLocal.role !== "assistant") return null;
    round = lastLocal.clarify ?? null;
  } else {
    let last: ThreadMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message !== undefined && message.role !== "system") {
        last = message;
        break;
      }
    }
    const persisted = last?.role === "assistant" ? (last.clarify_round ?? null) : null;
    if (persisted === null || persisted.status !== "open") return null;
    round = {
      round_id: persisted.round_id,
      kind: persisted.kind,
      questions: persisted.questions,
      allow_skip: persisted.allow_skip,
    };
  }
  if (round === null) return null;
  if (closedRoundIds[round.round_id] === true) return null;
  return round;
}

/**
 * May this round take the slot from the guest sign-up card (N9)? Answering a
 * clarify or quote round is free at the message wall (`clarify_answer`,
 * count_messages=False); a smart-rec answer is a `fetch_need`, which the wall
 * refuses (guest_gate.py:221-226). Every other stop (tokens, budget, guest
 * mode off) refuses both, so the sign-up card stays.
 */
export function roundMayDock(kind: ClarifyKind, stop: GuestStopCause | null): boolean {
  if (stop === null) return true;
  return stop === "messages" && (kind === "clarify" || kind === "quote");
}
